'use client';

/**
 * AgentHistoryPanel - Simulation history with improved layout
 *
 * Shows recent agent simulations in a card-based layout with:
 * - Status indicators (complete/error/running)
 * - Action badges with confidence
 * - Timestamps and duration
 * - Click to select and view details
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain agents
 */

import { useState, useEffect } from 'react';
import {
  Bot,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

interface SimulationSummary {
  simulation_id: string;
  entity_sid: string;
  entity_type: string;
  entity_name: string | null;
  status: string;
  final_action: string | null;
  final_action_type: string | null;
  confidence: string | null;
  created_at: string;
  total_ms: number | null;
}

export function AgentHistoryPanel() {
  const [simulations, setSimulations] = useState<SimulationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  useEffect(() => {
    fetchSimulations();
  }, []);

  const fetchSimulations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.getStrategyData<{ simulations: SimulationSummary[] }>('ki_simulation_list', { limit: 30 });
      if (!res.success) throw new Error(res.error || 'ki_simulation_list failed');
      setSimulations(res.data.simulations ?? []);
    } catch (err) {
      setError('Failed to load history');
      console.error('Failed to fetch simulations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (sim: SimulationSummary) => {
    selectNode({
      id: sim.simulation_id,
      type: 'simulation',
      label: sim.entity_name ?? sim.entity_sid,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getActionColor = (action: string | null) => {
    switch (action) {
      case 'execute':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'propose':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'escalate':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'defer':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const getConfidenceColor = (confidence: string | null) => {
    switch (confidence) {
      case 'high':
        return 'text-green-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-red-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Loading state
  if (isLoading && simulations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        <p className="text-sm">Loading history...</p>
      </div>
    );
  }

  // Error state
  if (error && simulations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <AlertTriangle className="w-8 h-8 mb-2 text-destructive opacity-70" />
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="ghost" size="sm" onClick={fetchSimulations} className="mt-2">
          <RefreshCw className="w-4 h-4 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  // Empty state
  if (simulations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Bot className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No simulations yet</p>
        <p className="text-xs mt-1 text-center">
          Use the search above to analyze an entity
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Recent Simulations</span>
          <Badge variant="secondary" className="text-xs">
            {simulations.length}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchSimulations}
          disabled={isLoading}
          className="h-7 px-2"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
        </Button>
      </div>

      {/* Simulation List */}
      <div className="flex-1 overflow-auto p-2 space-y-2">
        {simulations.map((sim) => {
          const isSelected = selectedNode?.id === sim.simulation_id;

          return (
            <button
              key={sim.simulation_id}
              onClick={() => handleSelect(sim)}
              className={cn(
                'w-full text-left p-3 rounded-lg border transition-all',
                'hover:bg-accent/50 hover:border-accent',
                isSelected
                  ? 'bg-accent border-primary ring-1 ring-primary/20'
                  : 'bg-card border-border'
              )}
            >
              {/* Top Row: Entity + Status */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {getStatusIcon(sim.status)}
                  <span className="font-medium text-sm truncate">
                    {sim.entity_name ?? sim.entity_sid}
                  </span>
                </div>
                <Badge variant="outline" className="text-[10px] shrink-0">
                  {sim.entity_type}
                </Badge>
              </div>

              {/* Middle Row: Action + Confidence */}
              {sim.final_action && (
                <div className="flex items-center gap-2 mt-2">
                  <Badge
                    variant="outline"
                    className={cn('text-xs', getActionColor(sim.final_action))}
                  >
                    {sim.final_action}
                    {sim.final_action_type && (
                      <span className="opacity-70 ml-1">: {sim.final_action_type}</span>
                    )}
                  </Badge>
                  {sim.confidence && (
                    <span className={cn('text-xs', getConfidenceColor(sim.confidence))}>
                      {sim.confidence}
                    </span>
                  )}
                </div>
              )}

              {/* Bottom Row: Time + Duration */}
              <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                <span>{formatTime(sim.created_at)}</span>
                <div className="flex items-center gap-1">
                  {sim.total_ms && (
                    <span>{(sim.total_ms / 1000).toFixed(1)}s</span>
                  )}
                  <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
